/**
 * SeoPositionsPage — `/seo/positions` (B2, Фаза 2). Позиции по ПС (Яндекс/Google
 * раздельно): доля в топ-3/топ-10 и средняя. Источник — съёмщик (Топвизор/Rush).
 * Без ключа съёмщика — карточка «не настроено» (Фаза 2 блокер).
 */

import { usePageHeader } from '@/components/layout/pageHeader';
import { HelpButton } from '@/components/layout/HelpButton';
import { Button, LoadingBlock, ErrorBlock, Badge } from '@/shared/ui';
import { useSeoPositions, useSeoPhase2Mutations, type PositionsByEngine } from '@/hooks/api/useSeo';

const HELP = {
  title: 'SEO — Позиции',
  why: 'Позиции по ключам/кластерам в Яндексе и Google раздельно, по регионам и устройствам. Показывает, растём ли мы в выдаче и где нас забирают zero-click/AI-ответы.',
  tasks: ['Смотреть долю в топ-3/топ-10 по ПС', 'Снимать позиции по регионам (Москва+миллионники) × устройствам', 'Ловить просадки'],
  how: ['Нажмите «Снять позиции» (нужен ключ Топвизор/Rush).', 'Смотрите распределение по ПС и динамику.'],
  glossary: [{ term: 'zero-click', def: 'ответ забирают без перехода на сайт (Алиса/AI Overviews)' }],
};

export function SeoPositionsPage() {
  usePageHeader({ title: 'SEO — Позиции', subtitle: 'Rank tracking (Яндекс + Google)', actions: <HelpButton help={HELP} /> });
  const { data, isLoading, isError, error, refetch } = useSeoPositions();
  const { refreshPositions } = useSeoPhase2Mutations();

  const latest = data?.latest;

  return (
    <div className="col gap20" style={{ maxWidth: 1050 }}>
      <div className="row spread wrap" style={{ gap: 8 }}>
        <span className="muted" style={{ fontSize: 13 }}>
          Снапшот: <span className="mono">{latest?.generatedAt ? new Date(latest.generatedAt).toLocaleString('ru-RU') : 'нет'}</span>
        </span>
        <Button variant="outline" size="sm" icon="Loader" onClick={() => refreshPositions.mutate()} disabled={refreshPositions.isPending}>
          {refreshPositions.isPending ? 'Снимаю…' : 'Снять позиции'}
        </Button>
      </div>

      {refreshPositions.isError && <ErrorBlock text={refreshPositions.error?.message} />}
      {isLoading && <LoadingBlock label="Загружаю позиции…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}

      {data && !latest && (
        <div className="card"><p className="muted" style={{ margin: 0 }}>Снапшота позиций ещё нет. Нажмите «Снять позиции».</p></div>
      )}

      {latest && !latest.configured && (
        <div className="card" style={{ borderLeft: '3px solid var(--warning, #CA8A04)' }}>
          <p className="muted" style={{ margin: 0 }}>
            <Badge tone="b-warning">съёмщик не настроен</Badge>{' '}
            Нет ключа Топвизор/Rush (TOPVIZOR_TOKEN/RUSH_TOKEN) — позиции по регионам не снимаются. Это блокер Фазы 2.
          </p>
        </div>
      )}

      {latest?.configured && (
        <section className="stat-grid">
          <EngineCard label="Яндекс" e={latest.byEngine.yandex} />
          <EngineCard label="Google" e={latest.byEngine.google} />
        </section>
      )}
    </div>
  );
}

function EngineCard({ label, e }: { label: string; e: PositionsByEngine }) {
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{label}</h3>
      <div className="stat-grid">
        <Mini label="Отслеживается" value={String(e.tracked)} />
        <Mini label="В топ-3" value={String(e.top3)} />
        <Mini label="В топ-10" value={String(e.top10)} />
        <Mini label="Ср. позиция" value={e.avg != null ? String(e.avg) : '—'} />
      </div>
    </div>
  );
}
function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={{ fontSize: 22 }}>{value}</div>
    </div>
  );
}
